import { Mutex } from 'async-mutex';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import axiosRetry, { exponentialDelay } from 'axios-retry';
import { stringify } from 'qs';

import {
  getSecurityTokens,
  removeSecurityTokens,
  setSecurityTokens,
} from '@/features/auth/authUtils';
import i18n from '@/lib/i18n/client';
import {
  type AvailableLanguageKeys,
  DEFAULT_LANGUAGE_KEY,
  backendCultures,
} from '@/lib/i18n/constants';
import type { JwtDto, RefreshTokenRequest } from '@/services/api';
import type { SecurityTokens } from '@/services/storage/schema';
import { logErrorToDataLayer } from '@/utils/errors';

export const apiMutex = new Mutex();

const apiService: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  paramsSerializer: {
    serialize: (params) => {
      return stringify(params, { arrayFormat: 'repeat' });
    },
  },
});

apiService.interceptors.request.use(setupCulture);
apiService.interceptors.request.use(setupToken);

apiService.interceptors.request.use(setupWaitForUnlock);
apiService.interceptors.response.use((x) => x, setupRefreshToken);
apiService.interceptors.response.use(replaceNextTokenWithNull);

apiService.interceptors.response.use((x) => x, dataLayerLog);
apiService.interceptors.request.use((x) => x, dataLayerLog);

async function replaceNextTokenWithNull(config: AxiosResponse) {
  if (config?.data?.pagedData?.nextToken === '%7b%7d') {
    config.data.pagedData.nextToken = null;
  }

  if (config?.data?.pagedData?.prevToken === '%7b%7d') {
    config.data.pagedData.prevToken = null;
  }

  return config;
}

async function setupCulture(config: InternalAxiosRequestConfig<ExplicitAny>) {
  const langKey = i18n.language as AvailableLanguageKeys;
  const culture =
    backendCultures[langKey] ?? backendCultures[DEFAULT_LANGUAGE_KEY];

  config.headers.set('x-culture', culture);
  return config;
}

async function setupToken(config: InternalAxiosRequestConfig<ExplicitAny>) {
  const securityTokens = getSecurityTokens();

  if (securityTokens?.jwtToken || config.overrideSecurityToken) {
    config.headers.set(
      'Authorization',
      `Bearer ${config.overrideSecurityToken ?? securityTokens?.jwtToken}`
    );
  }

  return config;
}

async function setupWaitForUnlock(
  config: InternalAxiosRequestConfig<ExplicitAny>
) {
  if (config.ignoreLock) return config;
  if (apiMutex.isLocked()) {
    console.debug('[Api Service] locked request, waiting for unlock');

    await apiMutex.waitForUnlock();
  }
  return config;
}

async function setupRefreshToken(error: ExplicitAny) {
  if (error.config?.ignoreRefresh) return Promise.reject(error);

  if (axios.isAxiosError(error) && error.response?.status === 401) {
    console.warn('[Api Service] 401 error, try to refresh token');
    const { config } = error;
    const originalRequest = config!;

    if (!apiMutex.isLocked()) {
      const securityTokens = getSecurityTokens();

      const release = await apiMutex.acquire();
      console.debug('[Api Service] lock acquire');

      if (
        !securityTokens ||
        !securityTokens.jwtToken ||
        !securityTokens.refreshToken
      ) {
        console.error(
          '[Api Service] securityTokens are not defined, logging out!'
        );
        // TODO what?
        release();
        return Promise.reject(error);
      }

      try {
        await tryToRefreshToken(securityTokens);
        // retry the initial query
        return await apiService({ ...originalRequest, ignoreLock: true });
      } catch (ex) {
        if ((ex as AxiosError)?.response?.status === 401) {
          console.error(
            '[Api Service] refresh token failed with 401, logging out'
          );
          // logout and reload
          removeSecurityTokens();
          window.location.reload();
        }

        console.error('[Api Service] refresh token failed!,\n', ex);
      } finally {
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      console.debug('wait until the mutex is available without locking it');
      await apiMutex.waitForUnlock();
      return await apiService(originalRequest);
    }
  }

  return Promise.reject(error);
}

export function dataLayerLog(axiosError: AxiosError) {
  if (axiosError?.response?.status === 401) {
    return Promise.reject(axiosError);
  }

  let errorType = 'Unknown Error';
  if (axiosError?.config && !axiosError?.response) {
    if (axiosError.request) {
      errorType = 'No Response from Server';
    } else {
      errorType = 'Request Configuration Error';
    }
  }

  if (axiosError?.response) {
    errorType = `HTTP Error ${axiosError.response.status}`;
  }

  const dataLayer = (window as ExplicitAny)?.dataLayer;
  if (dataLayer) {
    const headers = axiosError?.response?.headers;
    logErrorToDataLayer(axiosError, errorType, {
      userToken: headers?.['Authorization'],
    });
  }

  return Promise.reject(axiosError);
}

const tryToRefreshToken = async (securityTokens: SecurityTokens) => {
  axiosRetry(axios);

  const refreshResult = await axios.request<JwtDto>({
    url: `${import.meta.env.VITE_API_URL}${'/v1/auth/refresh-token'}`,
    method: 'POST',
    'axios-retry': {
      retries: 3,
      retryDelay: exponentialDelay,
    },
    headers: {
      Authorization: `Bearer ${securityTokens.jwtToken}`,
    },
    data: {
      refreshToken: securityTokens.refreshToken,
    } as RefreshTokenRequest,
  });

  console.info('[Api Service] refreshed the token successfully');
  setSecurityTokens({
    jwtToken: refreshResult.data.token!,
    refreshToken: refreshResult.data.refreshToken!,
  });
};

export { apiService };
